import * as React from "react";
import { forwardRef } from "react";

import { FileProps, UploadFileStatuses } from "../../../Engine/Core/ModalManager/Modals/Utils/Types";
import { useUploadFile } from "../../../Engine/Core/ModalManager/Modals/Utils/UploadFileRedisign/UseUploadFile";
import { FileException } from "../../../Engine/Core/ModalManager/Modals/Utils/UploadFileRedisign/UploadFile.styles";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { XML_UPLOAD_FILE } from "../../metricsAction";
import {
    backendConversionErrorMessage,
    formatLoadingErrorText,
    getAcceptFilesErrorFromName,
    invalidFileErrorText,
    loadFileErrorText,
} from "../../../Engine/Core/ModalManager/Modals/Utils/TextContent";
import { CustomFile } from "../../../Helpers/file";
import { UploadedFileInfo, UploadedFileStatus } from "../../../Engine/ActionCreators/FufUploadActionCreator";

export interface AttachmentFormUploadFileProps {
    onLoadAttachmentFile: (loadedFileProps: FileProps) => Promise<void>;
    requestedGfv: string;
    prepareIfNeedAndGetFileProperties: (file?: CustomFile) => Promise<UploadedFileInfo>;
    getPortalUserId: () => Promise<string>;
}

export const AttachmentFormUploadFile = forwardRef(function AttachmentFormUploadFile(
    props: AttachmentFormUploadFileProps,
    ref: React.ForwardedRef<HTMLElement>
): JSX.Element {
    const memoizedFilter = React.useCallback(filterValidFiles, []);
    const memoizedCheckValidFile = React.useCallback(checkValidFile, []);
    const track = useMetrics();
    const { prepareIfNeedAndGetFileProperties, onLoadAttachmentFile, requestedGfv, getPortalUserId } = props;

    const { filesContainer } = useUploadFile<string>({
        filterValidDriveUrl: memoizedFilter,
        checkValidFile: memoizedCheckValidFile,
        multiple: false,
        buttonType: "button",
        ref: ref,
        getPortalUserId: getPortalUserId,
        useExternDriveClient: true,
    });

    function filterValidFiles(fileToRemove: FileProps, url: string) {
        return url !== fileToRemove.driveUri;
    }

    async function checkValidFile(
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<string[]>>
    ): Promise<FileProps> {
        let errorMessageText: string = getAcceptFilesErrorFromName(file.uploadFile.name, ["xml", "xls", "xlsx"]) ?? "";

        const driveUriGuard = file.driveUri;
        if (file.id && driveUriGuard && !errorMessageText) {
            try {
                file.uploadFile.contentUri = driveUriGuard;
                const properties = await prepareIfNeedAndGetFileProperties(file.uploadFile);
                if (properties.uploadStatus === UploadedFileStatus.Unresolved) {
                    errorMessageText = invalidFileErrorText;
                } else if (properties.fileProperties === undefined) {
                    errorMessageText = loadFileErrorText;
                } else if (properties.fileProperties.gfv !== requestedGfv) {
                    errorMessageText = formatLoadingErrorText;
                } else {
                    file.uploadFile.contentUri = properties.fileProperties?.fufUri;
                    file.ndsTotalSum = properties.fileProperties?.ndsTotalSum;
                    await onLoadAttachmentFile(file);
                    setValidDriveURLs(value => [...value, driveUriGuard]);
                }
            } catch (e) {
                if (e instanceof Error && e.message) {
                    errorMessageText = e.message === backendConversionErrorMessage ? formatLoadingErrorText : e.message;
                } else {
                    errorMessageText = loadFileErrorText;
                }
            }
        }

        const status = errorMessageText ? UploadFileStatuses.error : UploadFileStatuses.success;
        track(XML_UPLOAD_FILE, {
            uploadFile: file.uploadFile.name,
            size: file.uploadFile.size,
            status: status,
            errorMessage: errorMessageText,
        });
        return {
            id: file.id,
            uploadFile: file.uploadFile,
            status: status,
            errorMessage: <FileException>{errorMessageText}</FileException>,
            driveUri: file.driveUri,
        };
    }

    return filesContainer;
});
